import { useExperimentalAccess } from "@/features/experiments/hooks/useExperimentalAccess";
import { useFetchExpertsOnLogin } from "@/features/venere";
import { getProducts } from "@/utils/apis/order-service";
import { getUserInfoApi } from "@/utils/apis/user-service";
import { ProductType } from "@/utils/enums/ProductType";
import { useAuthStore } from "@/utils/stores/useAuthStore";
import { useProductsStore } from "@/utils/stores/useProductsStore";
import { useUserStore } from "@/utils/stores/useUserStore";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export const useFetchUserData = () => {
    const { auth } = useAuthStore()
    const { updateUserData, userData } = useUserStore();
    const { updateProductsData } = useProductsStore();
    const { fetchExpertsOnLogin } = useFetchExpertsOnLogin();
    const navigate = useNavigate();
    
    // Use the new experimental access hook
    const { 
        isAdmin, 
        isVenere, 
        hasAnyAccess, 
        loading: accessLoading,
        error: accessError,
        refreshAccess 
    } = useExperimentalAccess();
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [isAdminRedirecting, setIsAdminRedirecting] = useState(false);
    const [isVenereRedirecting, setIsVenereRedirecting] = useState(false);
    const [userDataUpdated, setUserDataUpdated] = useState(false);
    const [accessCheckTimeout, setAccessCheckTimeout] = useState(false);
    const userEmailRef = useRef<string | null>(null);

    const fetchUserData = useCallback(async () => {
        if (!auth.userId || !auth.idToken) {
            setError(true);
            return;
        }
        setIsLoading(true);
        setError(false);
        setUserDataUpdated(false);
        setAccessCheckTimeout(false);
        
        try {
            const user = await getUserInfoApi(auth.userId, auth.idToken);
            userEmailRef.current = user.email;
            
            updateUserData({
                name: user.data.name,
                familyName: user.data.family_name,
                email: user.email,
                phoneNumber: user.data.mobile_phone,
                taxCode: user.tax_number,
                swabcode: user.swabcode,
                bloodcodes: user.bloodcodes,
                acceptedPrivacyVersion: user.accepted_privacy_version,
                acceptedPrivacyAt: user.accepted_privacy_at,
                d1Consent: user.d1_consent,
                d1ConsentAt: user.d1_consent_at,
            });

            // Mark that user data has been updated
            setUserDataUpdated(true);
        } catch (e) {
            setError(true);
        } finally {
            setIsLoading(false)
        }
    }, [auth.userId, auth.idToken, updateUserData]);

    // Set a timeout for access checking to prevent blocking regular users
    useEffect(() => {
        if (userDataUpdated && userData?.email && userEmailRef.current === userData.email && accessLoading) {
            const timeout = setTimeout(() => {
                setAccessCheckTimeout(true);
            }, 3000); // 3 second timeout

            return () => clearTimeout(timeout);
        }
    }, [userDataUpdated, userData?.email, accessLoading]);

    // Handle admin and Venere check and redirect after user data is updated
    useEffect(() => {
        if (userDataUpdated && userData?.email && userEmailRef.current === userData.email) {
            
            const handleUserFlow = async () => {
                // Wait for access checking to complete OR timeout
                if (accessLoading && !accessCheckTimeout) {
                    return;
                }
                
                // Check if user is admin using the new hook
                if (isAdmin) {
                    setIsAdminRedirecting(true);
                    // Show loading for a moment before redirecting
                    await new Promise((resolve) => setTimeout(resolve, 800));
                    navigate("/admin/dashboard");
                    return;
                }

                // Check if user is a Venere user
                if (isVenere) {
                    setIsVenereRedirecting(true);
                    
                    // Fetch experts for Venere users (don't block redirect if this fails)
                    try {
                        await fetchExpertsOnLogin();
                    } catch (expertError) {
                        console.warn('Failed to fetch experts for Venere user:', expertError);
                        // Don't fail the login process if expert fetching fails
                    }
                    
                    // Show loading for a moment before redirecting
                    await new Promise((resolve) => setTimeout(resolve, 800));
                    navigate("/venere/home");
                    return;
                }

                // For regular users, add a short wait for UX
                await new Promise((resolve) => setTimeout(resolve, 100));

                const products = await getProducts(auth.userId!, auth.idToken!);
                if (products && products.length > 0) {
                    products.forEach((product) => {
                        if (product.product_type === ProductType.DnaTest) {
                            updateProductsData({ dnaTest: product.data.channel });
                        }
                    });
                } else {
                }

                navigate("/check-purchases");
            };

            handleUserFlow();
        }
    }, [
        userDataUpdated, 
        userData?.email, 
        isAdmin, 
        isVenere, 
        hasAnyAccess,
        accessLoading,
        accessCheckTimeout,
        accessError,
        navigate, 
        auth.userId, 
        auth.idToken, 
        updateProductsData,
        fetchExpertsOnLogin
    ]);

    return { 
        fetchUserData, 
        error: error || accessError, 
        isLoading: isLoading || (accessLoading && !accessCheckTimeout), 
        isAdminRedirecting, 
        isVenereRedirecting,
        accessInfo: {
            isAdmin,
            isVenere,
            hasAnyAccess,
            accessLoading,
            accessError
        }
    };
};